import { inject, Injectable } from '@angular/core';
import { StoredAuthTokens } from '@kikkz/b04/auth/util';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';

@Injectable({
  providedIn: 'root',
})
export class AuthStorageService {
  private readonly AUTH_TOKENS_KEY = 'auth_tokens';
  private readonly PKCE_CODE_VERIFIER_KEY = 'pkce_code_verifier';
  private readonly AUTH_STATE_KEY = 'auth_state';
  private readonly loggingService = inject(LOGGING_SERVICE);

  saveAuthTokens(tokens: StoredAuthTokens): void {
    localStorage.setItem(this.AUTH_TOKENS_KEY, JSON.stringify(tokens));
  }

  getAuthTokens(): StoredAuthTokens | null {
    try {
      const raw = localStorage.getItem(this.AUTH_TOKENS_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);

      if (
        typeof parsed.access_token === 'string' &&
        typeof parsed.refresh_token === 'string' &&
        typeof parsed.id_token === 'string' &&
        typeof parsed.expires_in === 'number' &&
        typeof parsed.expiresAt === 'string'
      ) {
        return parsed as StoredAuthTokens;
      }

      this.loggingService.warn('Invalid token structure in localStorage');
      return null;
    } catch (e) {
      this.loggingService.error('Failed to parse auth tokens from localStorage:', e);
      return null;
    }
  }

  clearAuthTokens(): void {
    localStorage.removeItem(this.AUTH_TOKENS_KEY);
  }

  saveCodeVerifier(verifier: string): void {
    sessionStorage.setItem(this.PKCE_CODE_VERIFIER_KEY, verifier);
  }

  getCodeVerifier(): string | null {
    return sessionStorage.getItem(this.PKCE_CODE_VERIFIER_KEY);
  }

  clearCodeVerifier(): void {
    sessionStorage.removeItem(this.PKCE_CODE_VERIFIER_KEY);
  }

  saveAuthState(state: string): void {
    sessionStorage.setItem(this.AUTH_STATE_KEY, state);
  }

  getAuthState(): string | null {
    return sessionStorage.getItem(this.AUTH_STATE_KEY);
  }

  clearAuthState(): void {
    sessionStorage.removeItem(this.AUTH_STATE_KEY);
  }

  clearSessionVariables(): void {
    this.clearCodeVerifier();
    this.clearAuthState();
  }
}
